import React, { useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const NoopComponent = () => null;

const messages = defineMessages({
	showMore: {
		id: 'user-inline-preview.list.show-more',
		defaultMessage: 'Show more',
		description: 'Button label to expand and show more user cards in the list',
	},
	showLess: {
		id: 'user-inline-preview.list.show-less',
		defaultMessage: 'Show less',
		description: 'Button label to collapse and show fewer user cards in the list',
	},
});

const styles = cssMap({
	grid: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: token('space.100'),
	},
	stack: {
		display: 'grid',
		gridTemplateColumns: '1fr',
		gap: token('space.100'),
	},
	showMoreTrigger: {
		backgroundColor: 'transparent',
		paddingTop: '0',
		paddingBottom: '0',
		paddingLeft: '0',
		paddingRight: '0',
		width: 'auto',
	},
});

type UserInlinePreviewListLayout = 'grid' | 'stack';

interface UserInlinePreviewListProps {
	layout?: UserInlinePreviewListLayout;
	/**
	 * Caps how many child items are rendered. When set, extra children are omitted and no
	 * "Show more" control is shown (unlike `initialVisibleCount`).
	 */
	maxVisibleItems?: number;
	initialVisibleCount?: number;
	children: React.ReactNode;
	testId?: string;
}

/**
 * Renders a list of UserInlinePreview components in a grid or stack layout.
 * Use `initialVisibleCount` for expandable "Show more" behavior, or `maxVisibleItems` for a fixed cap
 * without expansion.
 *
 * @internal Not ready for consumption. Do not use in production.
 */
export const UserInlinePreviewList = componentWithFG(
	'pt_user_inline_preview_enabled',
	NoopComponent,
	UserInlinePreviewListComponent,
);

function UserInlinePreviewListComponent({
	layout = 'grid',
	maxVisibleItems,
	initialVisibleCount,
	children,
	testId = 'user-inline-preview-list',
}: UserInlinePreviewListProps) {
	const intl = useIntl();
	const [isExpanded, setIsExpanded] = useState(false);

	const childArray = React.Children.toArray(children);
	const isHardCapped = maxVisibleItems !== undefined;
	const cappedChildren = isHardCapped ? childArray.slice(0, maxVisibleItems) : childArray;
	const hasExpandOverflow =
		!isHardCapped && initialVisibleCount !== undefined && childArray.length > initialVisibleCount;
	const visibleChildren =
		hasExpandOverflow && !isExpanded ? childArray.slice(0, initialVisibleCount) : cappedChildren;

	const ChevronIcon = isExpanded ? ChevronDownIcon : ChevronRightIcon;

	return (
		<Stack space="space.075" testId={testId}>
			<Box xcss={layout === 'grid' ? styles.grid : styles.stack}>{visibleChildren}</Box>

			{hasExpandOverflow && (
				<Pressable
					xcss={styles.showMoreTrigger}
					onClick={() => setIsExpanded((prev) => !prev)}
					aria-expanded={isExpanded}
					testId={`${testId}-show-more`}
				>
					<Inline space="space.025" alignBlock="center">
						<ChevronIcon label="" color={token('color.icon.subtle')} />
						<Text color="color.text.subtle">
							{isExpanded
								? intl.formatMessage(messages.showLess)
								: intl.formatMessage(messages.showMore)}
						</Text>
					</Inline>
				</Pressable>
			)}
		</Stack>
	);
}
